import { Pressable, View, useWindowDimensions } from "@-/com.npmjs.react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import {
  TopSheet,
  topSheetSnapHeights,
  useTopSheetMotion,
} from "@-/dev.improve.haniverse.components.ui.top-sheet";
import { useGlassHeaderInset } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { ScrollEdgeTopExtensionContext } from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useCallback, useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react";

const HANDLE_HEIGHT = 32;
const CHART_GAP = 9;
const SHEET_BORDER_HEIGHT = 1;
const DEFAULT_VISIBLE_ROWS = 3;

/**
 * Reviews scroll inside a top sheet; the timeline stays fixed on the white surface below. Only the
 * user resizes the sheet: a new selection or chart window never moves it or the chart beneath it.
 */
export function DeckUpcomingScheduleLayout({
  chart,
  children,
  contentKey,
}: {
  chart: (onControlsHeightChange: (height: number) => void) => ReactNode;
  children: (
    bottomInset: number,
    onRowBoundariesChange: (boundaries: readonly number[], rowPitch: number) => void,
    scrollOffset: SharedValue<number>,
  ) => ReactNode;
  /** Identifies the cards the sheet shows; a change freezes the measured default height. */
  contentKey: string;
}) {
  const tabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const glassInset = useGlassHeaderInset();
  const topExtension = useContext(ScrollEdgeTopExtensionContext);
  const viewportExtension = glassInset === undefined ? 0 : topExtension;
  const { height: windowHeight } = useWindowDimensions();
  // Web server rendering has no window (height 0); the hydrating render must match its sheet height.
  const hasHydrated = useHasHydrated();
  const minimumPlotHeight = hasHydrated && windowHeight < 500 ? 40 : 88;
  const [controlsHeight, setControlsHeight] = useState(72);
  const [availableHeight, setAvailableHeight] = useState(600);
  const [savedPosition, setPosition] = useRouteBodyState<number | null>(
    "deck-reviews-row-sheet-position",
    null,
  );
  const [{ rowBoundaries, rowPitch }, setRows] = useState<{
    rowBoundaries: readonly number[];
    rowPitch: number;
  }>({ rowBoundaries: [], rowPitch: 0 });
  const onRowBoundariesChange = useCallback(
    (boundaries: readonly number[], pitch: number) =>
      setRows({ rowBoundaries: boundaries, rowPitch: pitch }),
    [],
  );
  const scrollOffset = useSharedValue(0);
  const maximum = Math.max(
    0,
    availableHeight +
      viewportExtension -
      tabFadeInset -
      controlsHeight -
      minimumPlotHeight -
      CHART_GAP -
      HANDLE_HEIGHT -
      SHEET_BORDER_HEIGHT,
  );
  const minimum = Math.min(maximum, (glassInset ?? 0) + 120);
  const lastRowBoundary = rowBoundaries.at(-1);
  // The clear viewport ends halfway through the gap after row three. Row four begins beneath
  // TopSheet's shared bottom blur; narrow/short screens select the nearest complete row that fits.
  // Fewer cards keep the same height: the gaps continue at the row pitch below the last row.
  const position =
    savedPosition ??
    (lastRowBoundary === undefined
      ? 1
      : Math.max(
          0,
          Math.min(
            1,
            ((rowBoundaries[DEFAULT_VISIBLE_ROWS - 1] ??
              lastRowBoundary + (DEFAULT_VISIBLE_ROWS - rowBoundaries.length) * rowPitch) -
              minimum) /
              Math.max(1, maximum - minimum),
          ),
        ));
  const previousContentKey = useRef(contentKey);
  useLayoutEffect(() => {
    if (previousContentKey.current === contentKey) {
      return;
    }
    previousContentKey.current = contentKey;
    // The default follows the first content's rows only. Keep that height before the new content
    // reports its own rows. Until rows are first measured, there is no default to keep.
    if (savedPosition === null && lastRowBoundary !== undefined) {
      setPosition(position);
    }
  }, [contentKey, lastRowBoundary, position, savedPosition, setPosition]);
  const motion = useTopSheetMotion({
    maximum,
    minimum,
    onPositionChange: setPosition,
    position,
    snapInterval: rowPitch,
    snapOffset: scrollOffset,
    snapPoints: rowBoundaries.length > 0 ? rowBoundaries : undefined,
  });
  const sheetHeight = motion.value;
  const moveByRow = (direction: 1 | -1) => {
    const current = sheetHeight.get();
    const candidates = topSheetSnapHeights(
      rowBoundaries,
      minimum,
      maximum,
      scrollOffset.get(),
      rowPitch,
    );
    const next =
      direction === 1
        ? (candidates.find((height) => height > current + 1) ?? candidates.at(-1))
        : ([...candidates].reverse().find((height) => height < current - 1) ?? candidates[0]);
    if (next !== undefined) {
      setPosition((next - minimum) / Math.max(1, maximum - minimum));
    }
  };
  const chartStyle = useAnimatedStyle(() => ({
    // Share the sheet's UI-thread geometry so the gap stays fixed throughout drags and springs.
    top:
      -viewportExtension +
      Math.max(0, sheetHeight.get()) +
      HANDLE_HEIGHT +
      SHEET_BORDER_HEIGHT +
      CHART_GAP,
  }));
  return (
    <View
      className="min-h-0 flex-1 bg-surface"
      onLayout={({ nativeEvent: { layout } }) => setAvailableHeight(layout.height)}
      testID="deck-upcoming-schedule-layout"
    >
      <TopSheet
        handle={
          <Pressable
            accessibilityActions={[
              { label: "Expand scheduled reviews", name: "increment" },
              { label: "Collapse scheduled reviews", name: "decrement" },
            ]}
            accessibilityHint="Drag down to expand reviews or up to collapse them."
            accessibilityLabel="Scheduled reviews sheet"
            accessibilityRole="adjustable"
            accessibilityValue={{ max: 1, min: 0, now: position }}
            className="h-8 items-center justify-center"
            onAccessibilityAction={(event) =>
              moveByRow(event.nativeEvent.actionName === "increment" ? 1 : -1)
            }
            onPress={() => setPosition(position === 0 ? 1 : 0)}
            testID="deck-upcoming-sheet-handle"
          >
            <View className="h-1 w-10 rounded-full bg-muted-foreground/40" />
          </Pressable>
        }
        motion={motion}
        style={{ top: -viewportExtension }}
        testID="deck-upcoming-top-sheet"
      >
        {children(HANDLE_HEIGHT + 4, onRowBoundariesChange, scrollOffset)}
      </TopSheet>
      <Animated.View
        className="absolute inset-x-0 bg-surface"
        style={[{ bottom: tabFadeInset }, chartStyle]}
        testID="deck-upcoming-chart-chrome"
      >
        {chart(setControlsHeight)}
      </Animated.View>
    </View>
  );
}
